import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { RenewalRemindersSection } from './RenewalRemindersSection';
import { ClientService } from './useData';
import { cn } from '../../lib/utils';
import { Button, Card, XIcon } from '../../components/ui';

export type RenewalRemindersPanelVariant = 'panel' | 'slideover' | 'modal';

export interface ServiceRenewalRemindersPanelProps {
  open: boolean;
  onClose: () => void;
  serviceItem: ClientService | null;
  onReminderAdded?: () => void;
  /** When true, render as side panel in layout (no backdrop, no fixed positioning) */
  asPanel?: boolean;
  /** Override display: 'panel' (inline), 'slideover' (slide from right), 'modal' (centered dialog) */
  variant?: RenewalRemindersPanelVariant;
}

const ServiceRenewalRemindersPanel: React.FC<ServiceRenewalRemindersPanelProps> = ({
  open,
  onClose,
  serviceItem,
  onReminderAdded,
  asPanel = false,
  variant: variantProp,
}) => {
  const { colors } = useTheme();
  if (!open) return null;

  const variant: RenewalRemindersPanelVariant = variantProp ?? (asPanel ? 'panel' : 'slideover');
  const showBackdrop = variant === 'slideover' || variant === 'modal';
  const isModal = variant === 'modal';

  const serviceId = serviceItem?.clientServicetId;

  const content = (
    <>
        {/* Header */}
        <div className={cn('flex-shrink-0 flex items-start justify-between gap-3 px-5 py-4 border-b', colors.border, colors.headerBg)}>
          <div className="min-w-0 flex-1 flex items-start gap-3">
            <div className={cn('flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center', colors.surfaceHover)}>
              <svg className={cn('w-5 h-5', colors.primaryFg)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className={cn('text-lg font-semibold', colors.text)}>Renewal reminders</h2>
              {serviceItem && (
                <>
                  <p className={cn('mt-0.5 text-sm truncate', colors.textSecondary)} title={serviceItem.serviceDescr ?? undefined}>
                    {serviceItem.serviceDescr || 'Service'}
                  </p>
                  {serviceItem.client?.clientName && (
                    <p className={cn('text-xs mt-0.5', colors.textMuted)}>Client: {serviceItem.client.clientName}</p>
                  )}
                </>
              )}
            </div>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close" className="flex-shrink-0">
            <XIcon className="w-5 h-5" />
          </Button>
        </div>
        <div className={cn('flex-1 overflow-y-auto p-5 min-h-0')}>
          {serviceId != null ? (
            <RenewalRemindersSection clientServiceId={serviceId} onReminderAdded={onReminderAdded} variant="panel" />
          ) : (
            <Card variant="outlined" className={cn('rounded-xl border-dashed p-8 text-center', colors.border)}>
              <div className={cn('w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3', colors.surfaceHover)}>
                <svg className={cn('w-6 h-6', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <p className={cn('text-sm', colors.textMuted)}>Select a service to view renewal reminders.</p>
            </Card>
          )}
        </div>
    </>
  );

  const dialogBox = (
    <div
      className={cn(
        'flex flex-col',
        colors.surface,
        colors.border,
        'border rounded-xl shadow-2xl',
        variant === 'panel' && 'h-full min-h-0 w-full',
        variant === 'slideover' && 'fixed top-0 right-0 bottom-0 z-50 w-full max-w-xl border-l animate-in slide-in-from-right duration-300',
        isModal && 'w-full max-w-lg max-h-[85vh] z-50'
      )}
      role="dialog"
      aria-label="Renewal reminders"
    >
      {content}
    </div>
  );

  return (
    <>
      {showBackdrop && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
          aria-hidden
        />
      )}
      {isModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={(e) => e.target === e.currentTarget && onClose()}>
          {dialogBox}
        </div>
      ) : (
        dialogBox
      )}
    </>
  );
};

export default ServiceRenewalRemindersPanel;
